import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const STORAGE_KEY = "ui.sidebarCollapsed";
const SidebarContext = createContext(null);

const readCollapsed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const isMobileViewport = () => window.matchMedia("(max-width: 991.98px)").matches;

/**
 * `collapsed`  — desktop icon-rail mode (remembered per browser).
 * `mobileOpen` — off-canvas drawer state below the lg breakpoint.
 * `toggleSidebar` does the right thing for the current viewport.
 */
export const SidebarProvider = ({ children }) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* storage unavailable — keep in-memory state only */
      }
      return next;
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isMobileViewport()) setMobileOpen((prev) => !prev);
    else toggleCollapsed();
  }, [toggleCollapsed]);

  const closeSidebar = useCallback(() => setMobileOpen(false), []);

  const value = useMemo(
    () => ({ collapsed, mobileOpen, toggleSidebar, toggleCollapsed, closeSidebar }),
    [collapsed, mobileOpen, toggleSidebar, toggleCollapsed, closeSidebar]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = () => useContext(SidebarContext);
